/**
 * MIT License
 * For full license information, please view the LICENSE file that was distributed with this source code.
 */
import '@stream-io/stream-chat-css/dist/css/index.css';
import 'rsuite/dist/rsuite.min.css';
import 'styles/index.css';
import React, { useEffect, useRef, useState } from 'react';

import { getClient } from '@amazd/common/apollo';
import RingingPopup from '@amazd/common/components/RingingPopup';
import VideoCallView from '@amazd/common/components/VideoChatView';
import { VideoCallProvider } from '@amazd/common/components/VideoChatView/contexts/videoCallContext';
import { useAmplitudeEvents } from '@amazd/common/hooks/useAmplitudeEvents';
import store from '@amazd/common/redux/store';
import { createTheme } from '@amazd/common/theme';
import { reportError } from '@amazd/common/utils';
import { trackCallEnded, trackCallJoined } from '@amazd/common/utils/analytics';
import { getAuthStoreState, logoutUser } from '@amazd/common/utils/auth';
import { AppConfigProvider, useAppConfig } from '@amazd/common/utils/env';
import { I18nProvider } from '@amazd/common/utils/i18n';
import { gql } from '@apollo/client';
import { CssBaseline, StyledEngineProvider } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import ErrorFallback from 'components/ErrorFallback';
import VerifySufficientScreenSize from 'components/VerifySufficientScreenSize';
import { HeadProvider } from 'contexts/headContext';
import { initialContext as ModalInitialContext, ModalContextValue, ModalProvider } from 'contexts/modalContext';
import App from 'next/app';
import { ErrorBoundary } from 'react-error-boundary';
import { Provider } from 'react-redux';
import { isServer } from 'utils';
import { backendMapEnvToFrontend } from 'utils/env';
import { getFirebaseCloudMessaging } from 'utils/firebase';
import ModalView from 'views/ModalView';

async function amazdParticipantIds(amazdId: string): Promise<null | { expert_id: string; shopper_id: string }> {
  const { data } = await getClient().query({
    query: gql`
      query ($id: String!) {
        amazd(id: $id) {
          owner {
            id
          }
          expert {
            id
          }
        }
      }
    `,
    variables: { id: amazdId },
  });
  if (!data) return null;
  return {
    expert_id: data.amazd?.expert?.id,
    shopper_id: data.amazd?.owner?.id,
  };
}

function AmplitudeEvents() {
  const { env } = useAppConfig();
  useAmplitudeEvents({
    client_url: window.location.origin,
    company_handle: getAuthStoreState().getUserType()?.company?.handle || '',
    source: 'web',
    apiKey: env?.AMPLITUDE_API_KEY as string,
  });
  return null;
}

const MyApp = ({ Component, pageProps, appEnv }: any): JSX.Element => {
  const modal = useState<ModalContextValue>(ModalInitialContext);
  const [theme] = useState(createTheme());
  const currentCallInfo = useRef({ startedAt: 0, amazdId: '', expert_id: '', shopper_id: '' });
  const firebaseCloudMessaging = getFirebaseCloudMessaging(appEnv);

  useEffect(() => {
    // Remove the server-side injected CSS.
    const jssStyles = document.querySelector('#jss-server-side');
    if (jssStyles && jssStyles.parentElement) {
      jssStyles.parentElement.removeChild(jssStyles);
    }

    // Listen for unhandled rejection errors
    const unhandledRejectionListener = (e: any) => {
      reportError(e.reason);
    };
    window.addEventListener('unhandledrejection', unhandledRejectionListener);

    return () => {
      window.removeEventListener('unhandledrejection', unhandledRejectionListener);
    };
  }, []);

  // Listen storage changes from another window.
  // This will help to log out user in all windows
  // if in any of the window user gets logged out
  React.useEffect(() => {
    const handleLogoutFromAnotherTab = (e: any) => {
      if (!e.key) {
        try {
          // unsubscribe notifications
          firebaseCloudMessaging.unsubscribe();
          logoutUser();
        } catch (error) {
          if (process.env.NODE_ENV === 'development') {
            throw error;
          }
        }
      }
    };
    window.addEventListener('storage', handleLogoutFromAnotherTab);

    return () => window.removeEventListener('storage', handleLogoutFromAnotherTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    firebaseCloudMessaging.init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleError = (error: Error, info: { componentStack: string }) => {
    reportError(error, {
      userId: getAuthStoreState().user?.id,
      extraInfo: info,
    });
  };

  const handleOnCallEnded = (amazdId: string) => {
    if (amazdId === currentCallInfo.current.amazdId) {
      trackCallEnded({
        channel_id: currentCallInfo.current.amazdId,
        expert_id: currentCallInfo.current.expert_id,
        shopper_id: currentCallInfo.current.shopper_id,
        duration: '0',
      });
    }
    currentCallInfo.current = { amazdId: '', expert_id: '', shopper_id: '', startedAt: 0 };
  };

  const handleOnCallStarted = async (amazdId: string) => {
    const info = await amazdParticipantIds(amazdId);
    if (info) {
      const { expert_id, shopper_id } = info;
      currentCallInfo.current = { amazdId, expert_id, shopper_id, startedAt: +new Date() };
      trackCallJoined({
        channel_id: amazdId,
        expert_id: info.expert_id,
        shopper_id: info.expert_id,
      });
    }
  };

  return (
    <AppConfigProvider env={appEnv}>
      {!isServer() && <AmplitudeEvents />}
      <HeadProvider>
        <Provider store={store}>
          <ErrorBoundary FallbackComponent={ErrorFallback} onError={handleError}>
            <I18nProvider settings={{ hideIfNotReady: true, autoLoadResources: true }}>
              <StyledEngineProvider injectFirst>
                <ThemeProvider theme={theme}>
                  <CssBaseline />
                  <ModalProvider value={modal}>
                    <ModalView />
                    <VideoCallProvider
                      isWidgetScreen={false}
                      onCallEnded={handleOnCallEnded}
                      onCallStarted={handleOnCallStarted}
                    >
                      <RingingPopup />
                      <VideoCallView />
                      <VerifySufficientScreenSize>
                        <Component {...pageProps} />
                      </VerifySufficientScreenSize>
                    </VideoCallProvider>
                  </ModalProvider>
                </ThemeProvider>
              </StyledEngineProvider>
            </I18nProvider>
          </ErrorBoundary>
        </Provider>
      </HeadProvider>
    </AppConfigProvider>
  );
};

const getInitialProps: typeof App.getInitialProps = async (appContext) => {
  const {
    ctx: { req, res },
  } = appContext;
  if (req && res && req.headers['x-forwarded-proto'] === 'http') {
    // eslint-disable-next-line
    res.writeHead(301, { Location: `https://${req.headers.host}${req.url}` });
    res.end();
    return { pageProps: {} };
  }
  return {
    ...(await App.getInitialProps(appContext)),
    appEnv: isServer() ? backendMapEnvToFrontend() : undefined,
  };
};

MyApp.getInitialProps = getInitialProps;

export default MyApp;
